/*
  This class provides a control-flow API for your phone verification routine.
  It has a lot of assumptions about the implementation:
    requires jquery library;
    requires parsley validation library;
    requires intl-tel-input library;
    expects #verify-phone-input to be present on the page as a phone numbers source
    expects a user form with id #leadform to be present as a user general data source
    expects all of the inputs to be wrapped in a form that has parsley validations enabled
*/
export class PhoneVerifier {
  // You can pass your own selectors if you need to override the defaults. Custom selectors should be a valid class or id jQ selector
  constructor(customSelectors = {}) {
    this.$phoneInput = $(customSelectors.phoneInputSelector || '#verify-phone-input');
    this.$codeInputsContainer = $(customSelectors.codeContainerSelector || '.code-field');
    this.$codeInputs = $(customSelectors.codeInputsSelector || '.code-input');
    this.$leadForm = $(customSelectors.leadFormSelector || '#leadform');
    this.$verifyCodeScreen = $(customSelectors.verifyCodeScreenSelector || '#verify-code-screen');
    this.$editPhoneScreen = $(customSelectors.editPhoneScreenSelector || '#edit-phone-screen');
    this.$confrimationScreen = $(customSelectors.customScreenSelector || '#confirmation-screen');
    this.$customScreen = $(customSelectors.customScreenSelector || '#custom-screen');
    this.$skipButton = $(customSelectors.skipButtonSelector || '.skip-button');
    this.$errorContainer = $(customSelectors.errorContainerSelector || '.error-message-container');
    this.$errorMessage = $(customSelectors.errorContainerSelector || '.error-message');
    this.authenticityTokenInput = $('input[name="authenticity_token"]');
    this.userId = null;
    this.hijackSubmissionCntrlFunc = null;
    this.attemptsCounter = 0;
    this.isLandline = false;
    this.clearHijackFunc = true;

    this.#setupCodeInputEvents();
  }

  async sendVerificationCode() {
    this.$phoneInput.parsley().validate();
    if (!this.$phoneInput.parsley().isValid()) {
      return;
    } else {
      this.#clearErrors();
    }

    let updateResponse = await this.updateUsersPhone();
    if (updateResponse.skipVerification) {
      this.proceedSubmission(false)
      return
    } else if (!updateResponse.isValid) {
      this.#setPhoneError("The entered phone number couldn't be verified. Check if your phone number is correct, or enter a valid one.");
      return
    }

    try {
      await $.ajax({
        url: '/users/send_verification_code',
        type: 'POST',
        dataType: 'json',
        data: { phone: this.$phoneInput.val(), authenticity_token: this.authenticityTokenInput.val() },
        success: (response) => {
          if ($('.phone-legend-span').length) { $('.phone-legend-span').text(this.$phoneInput.val()); }
          this.isLandline = response.landline;
          this.#clearCodeInputs();
          this.showConfirmCodePanel();
          this.attemptsCounter = 0
          this.#toggleSkipButton();
        },
        error: (response) => {
          if (response.responseJSON.skip_verification) {
            this.proceedSubmission(false);
          } else {
            this.#setPhoneError(response.responseJSON.error);
            this.#setCodeError(response.responseJSON.error);
            this.attemptsCounter += 1
            this.#toggleSkipButton();
          }
        }
      })
    } catch (error) {
      console.error(error);
    }
  }

  resendVerificationCode() {
    this.#clearCodeInputs();
    this.sendVerificationCode();
  }

  verifyCode(button) {
    let code = this.$codeInputs.map(function() {
      let matches = $(this).val().match(/\d/);
      return matches ? matches[0] : null
    }).get().join('');

    if (code.length !== 6) {
      this.#clearCodeInputs();
      this.#setCodeError('Invalid Code');
      return;
    } else {
      this.#clearErrors();
    }

    $.ajax({
      url: '/users/check_verification_code',
      type: 'POST',
      dataType: 'json',
      data: { phone: this.$phoneInput.val(), authenticity_token: this.authenticityTokenInput.val(), code },
      success: (response) => {
        this.attemptsCounter = 0
        this.$codeInputsContainer.addClass('valid');
        this.#disableButton(button);
        this.proceedSubmission(true);
      },
      error: (response) => {
        if (response.responseJSON.skip_verification) {
          this.proceedSubmission(false);
        } else {
          this.#clearCodeInputs();
          this.#setCodeError(response.responseJSON.error);
          this.attemptsCounter += 1
          this.#toggleSkipButton();
        }
      }
    })
  }

  proceedSubmission(isVerified) {
    if (isVerified) {
      window._vis_opt_queue = window._vis_opt_queue || [];
      window._vis_opt_queue.push(function() {_vis_opt_goal_conversion(201);});
      this.$leadForm.append(`<input type="hidden" name="user[sms_confirmed_at]" value=${new Date().toISOString()}>`) 
    }

    if (this.hijackSubmissionCntrlFunc) {
      let proceedSubmissionFunc = () => { 
        this.$leadForm.submit(); 
      }

      this.hijackSubmissionCntrlFunc(proceedSubmissionFunc); // if there is hijacking function assigned -> call it with submission trigger and let it handle the submission
      this.hijackSubmissionCntrlFunc = null; // clear hijacking function so that the next call to proceedSubmission will trigger the actual submission (used when user closes the modal to actuall complete the flow)
    } else {
      this.$leadForm.submit();
    }
  }

  showEditPhonePanel() {
    this.attemptsCounter = 0
    this.#clearErrors();
    this.$verifyCodeScreen.hide();
    this.$customScreen.hide();
    this.$confrimationScreen.hide();
    this.$editPhoneScreen.show();
  }

  showConfirmCodePanel() {
    if (this.isLandline) {
      this.$verifyCodeScreen.find('#sms-verification-confirm-message').css('display', 'none');
      this.$verifyCodeScreen.find('#voice-verification-confirm-message').css('display', 'flex');
    } else {
      this.$verifyCodeScreen.find('#sms-verification-confirm-message').css('display', 'flex');
      this.$verifyCodeScreen.find('#voice-verification-confirm-message').css('display', 'none');
    }
    this.attemptsCounter = 0
    this.#clearErrors();
    this.$editPhoneScreen.hide();
    this.$customScreen.hide();
    this.$confrimationScreen.hide();
    this.$verifyCodeScreen.show();
    $(this.$codeInputs[0]).focus();
  }

  showConfirmationPanel() {
    this.attemptsCounter = 0
    this.$editPhoneScreen.hide();
    this.$verifyCodeScreen.hide();
    this.$customScreen.hide();
    this.$confrimationScreen.show();
  }

  drawCustomPanel(content) {
    this.$customScreen.html(content);
  }

  showCustomPanel() {
    this.$editPhoneScreen.hide();
    this.$verifyCodeScreen.hide();
    this.$customScreen.show();
  }

  async updateUsersPhone() {
    if (!this.userId) { return };
    
    let responseObj = {}

    try {
      await $.ajax({
        url: `/users/${this.userId}/update_phone_number`,
        type: 'PATCH',
        dataType: 'json',
        data: { phone: this.$phoneInput.val(), authenticity_token: this.authenticityTokenInput.val() },
        success: (response) => {
          responseObj['isValid'] = !response.error;
          responseObj['skipVerification'] = response.skip_verification;
        },
        error: () => {
          responseObj['isValid'] = false;
          responseObj['skipVerification'] = false;
        }
      })
    } catch (error) {
      responseObj['isValid'] = false;
      responseObj['skipVerification'] = false;
    }

    return responseObj
  }

  #setupCodeInputEvents() {
    const $inpts = this.$codeInputs;

    $inpts.on({
      focus() { this.select() },
      input(event) {
        if (this.value && !this.value.match(/\d/)) {
          $(this).val(''); // allow only digits, clear if it's not a digit
          return event.preventDefault();
        }
        const idx = $inpts.index(this);
        if (this.value) {
          $inpts.eq(idx+1).focus();
        }
      },
      keydown(event) { // handle deleting
        const idx = $inpts.index(this);
        if (!this.value && event.key === 'Backspace' && idx) {
          $inpts.eq(idx-1).focus();
        }
        if (event.key === 'ArrowLeft' && idx) {
          event.preventDefault();
          $inpts.eq(idx-1).focus().select();
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          $inpts.eq(idx+1).focus().select();
        }
      },
      keyup() {
        let code = $inpts.map(function() {
          return $(this).val();
        }).get().join('');

        if (code.length === 6) {
          $('.verify-code-trigger').removeAttr('disabled');
        } else {
          $('.verify-code-trigger').attr('disabled', 'disabled');
        }
      },
      paste(event) {
        const pastedData = event.originalEvent.clipboardData.getData('text').trim();
        // split string to array or characters
        const code = pastedData.match(/\d/g);
        // Check if 6 digits are present
        if (code.length < 6) { return event.preventDefault(); }
        // populate inputs
        $inpts.val(i => code[i]);
        event.preventDefault();
        $inpts.last().focus();
      },
    });
  }

  #setCodeError(error) {
    this.#setError(error);
    this.$codeInputsContainer.addClass('invalid');
    this.$codeInputs.first().focus();
  }

  #clearErrors() {
    $(this.$codeInputs[0]).parsley().reset();
    this.$phoneInput.parsley().reset();
    this.$codeInputsContainer.removeClass('invalid');
    this.$errorMessage.html('');
    this.$errorContainer.hide();
  }

  #setPhoneError(error) {
    this.#setError(error);
  }

  #setError(error) {
    this.$errorMessage.html(error);
    this.$errorContainer.show();
  }

  #clearCodeInputs() {
    this.$codeInputs.val('');
  }

  #disableButton(button) {
    const btnText = $(button).text();
    $(button).data('enable-with', btnText)
      .attr('disabled', 'disabled')
      .text($(button).data('disable-with'));
  }

  #toggleSkipButton() {
    if (this.attemptsCounter > 1) {
      this.$skipButton.show();
    } else {
      this.$skipButton.hide();
    }
  }
}
